/* eslint-disable @typescript-eslint/no-explicit-any */
import StatusBadge from '@/components/common/StatusBadge';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import { getCurrencySymbol } from '@/utils/currencyUtils';
import { Card, Flex, Text, VStack, Spinner, Box } from '@chakra-ui/react';
import { Table, Thead, Tbody, Tr, Th, Td, TableContainer } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { useGetAllPaymentHistory } from '@/services/OrderManagement';
import { useMemo } from 'react';
import { TAX_RATE } from '@/utils/constants';

interface RecentOrderItem {
  order_id: string;
  quantity: number;
  total_price: number;
  currency: string;
  order_status: {
    status_name: string;
  };
}

const RecentOrder = () => {
  const { data, isLoading } = useGetAllPaymentHistory({
    page: 1,
    page_size: 5,
  });

  const recentOrders = useMemo<RecentOrderItem[]>(() => {
    if (!data?.results || data.results.length === 0) return [];

    const orders: RecentOrderItem[] = [];

    for (const payment of data.results) {
      const items = payment.purchased_items || [];

      if (items.length === 0) continue;

      // Calculate total quantity
      const totalQuantity = items.reduce((sum: number, item: any) => {
        return sum + (item.cart_item?.quantity || 0);
      }, 0);

      // Calculate price summary
      const itemsSummary = items.reduce(
        (acc: { productTotal: number; customizedTotal: number; addOnTotal: number }, item: any) => {
          const txn = item.order_tracking?.individual_txn;
          const cartQty = item.cart_item?.quantity || 1;
          const qty = txn?.quantity || cartQty;

          const unitPrice = parseFloat(txn?.unit_price?.toString() || '0');
          const customizedPrice = parseFloat(txn?.customized_price?.toString() || '0');
          const addOnPrice = parseFloat(txn?.add_ons_price?.toString() || '0');

          acc.productTotal += unitPrice * qty;
          acc.customizedTotal += customizedPrice * qty;
          acc.addOnTotal += addOnPrice * qty;

          return acc;
        },
        { productTotal: 0, customizedTotal: 0, addOnTotal: 0 },
      );

      const baseSubtotal =
        itemsSummary.productTotal + itemsSummary.customizedTotal + itemsSummary.addOnTotal;

      const estimatedTax = baseSubtotal * TAX_RATE;
      const totalPrice = parseFloat((baseSubtotal + estimatedTax).toFixed(2));

      // Status aggregation logic
      const statuses = items.map((item: any) => {
        const tracking = item.order_tracking || {};
        const statusName = tracking.order_status?.status_name || 'Processing';

        return {
          statusName,
          isUnderMaintenance: statusName === 'Under Maintenance',
          isReadyForDelivery: statusName === 'Ready for Delivery',
          isShipped: statusName === 'Shipped',
          isInWarehouse: tracking.is_in_warehouse === true,
          isOutForDelivery: statusName === 'Out for Delivery',
          isDelivered: tracking.has_delivered === true,
          isDeliveryFailed: tracking.has_delivery_failed === true,
          isCancelled: tracking.has_cancelled === true,
        };
      });

      let displayStatus = 'Not Shipped Yet';

      if (statuses.some((s: any) => s.isUnderMaintenance)) {
        displayStatus = 'Under Maintenance';
      } else if (statuses.every((s: any) => s.isReadyForDelivery)) {
        displayStatus = 'Ready for Delivery';
      } else if (statuses.every((s: any) => s.isDelivered || s.isDeliveryFailed || s.isCancelled)) {
        const hasDelivered = statuses.some((s: any) => s.isDelivered);
        const hasDeliveryFailed = statuses.some((s: any) => s.isDeliveryFailed);

        if (hasDelivered && hasDeliveryFailed) {
          displayStatus = 'Mixed Final Status';
        } else if (hasDelivered) {
          displayStatus = 'Delivered';
        } else if (hasDeliveryFailed) {
          displayStatus = 'Delivery Failed';
        } else {
          displayStatus = 'Cancelled';
        }
      } else if (statuses.some((s: any) => s.isShipped || s.isInWarehouse || s.isOutForDelivery)) {
        displayStatus = 'Delivery In Progress';
      }

      orders.push({
        order_id: payment.bill_id || '-',
        quantity: totalQuantity,
        total_price: totalPrice,
        currency: payment.currency || 'USD',
        order_status: {
          status_name: displayStatus,
        },
      });
    }

    return orders;
  }, [data]);

  return (
    <Card
      shadow="none"
      h={430}
      borderColor="border.default60"
      fontFamily="primary"
      borderWidth="1px"
    >
      <Flex justify="space-between" p={4}>
        <Text fontSize="xl" fontWeight="semibold">
          Recent Orders
        </Text>
        <Box
          as={Link}
          to={NAVIGATION_ROUTES.ALL_ORDERS}
          display="inline-block"
          _focusVisible={{
            background: 'brand.primary.50',
            color: 'brand.primary.DEFAULT',
            outline: '2px solid',
            outlineColor: 'brand.primary.DEFAULT',
            outlineOffset: '2px',
            boxShadow: 'none',
            borderRadius: '4px',
            textDecoration: 'none',
          }}
        >
          <Text fontSize="md" fontWeight="semibold" color="brand.primary.DEFAULT">
            View All
          </Text>
        </Box>
      </Flex>

      {isLoading ? (
        <VStack height="360px" width="100%" justifyContent="center" alignItems="center">
          <Spinner />
        </VStack>
      ) : recentOrders.length === 0 ? (
        <VStack height="360px" width="100%" justifyContent="center" alignItems="center">
          <VStack spacing={1}>
            <Text color="text.primary" fontSize="xl" fontWeight="semibold">
              No data available
            </Text>
            <Text color="text.disabled" fontSize="sm">
              There are no recent orders to display.
            </Text>
          </VStack>
        </VStack>
      ) : (
        <TableContainer
          _focusVisible={{
            background: 'brand.primary.50',
            color: 'brand.primary.DEFAULT',
            outline: '2px solid',
            outlineColor: 'brand.primary.DEFAULT',
            outlineOffset: '2px',
            boxShadow: 'none',
            borderRadius: '4px',
            textDecoration: 'none',
          }}
        >
          <Table variant="simple">
            <Thead bg="background.secondary">
              <Tr>
                <Th fontWeight="semibold" textTransform="capitalize" textAlign="start">
                  Order ID
                </Th>
                <Th fontWeight="semibold" textTransform="capitalize" textAlign="start">
                  Qty
                </Th>
                <Th fontWeight="semibold" textTransform="capitalize" textAlign="start">
                  Amount
                </Th>
                <Th fontWeight="semibold" textTransform="capitalize" textAlign="start">
                  Status
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {recentOrders.map((order) => (
                <Tr key={order.order_id}>
                  <Td textAlign="start">{order.order_id}</Td>
                  <Td textAlign="start">{order.quantity}</Td>
                  <Td>
                    {order.currency && order.total_price !== undefined
                      ? `${getCurrencySymbol(order.currency)}${order.total_price.toFixed(2)}`
                      : '-'}
                  </Td>
                  <Td>
                    <StatusBadge status={order.order_status.status_name ?? 'Unknown'} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Card>
  );
};

export default RecentOrder;
